/**
 * Erros de domínio. Mapeados para status HTTP na borda da API, para que o
 * tratamento de erro seja consistente em todas as rotas.
 */

export type DomainErrorCode =
  | "VALIDATION"
  | "NOT_FOUND"
  | "FORBIDDEN"
  | "UNAUTHORIZED"
  | "CONFLICT"
  | "VERSION_CONFLICT"
  | "RATE_LIMITED"
  | "GONE"
  | "PAYLOAD_TOO_LARGE"
  | "UNSUPPORTED_MEDIA_TYPE";

const HTTP_STATUS: Record<DomainErrorCode, number> = {
  VALIDATION: 422,
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  UNAUTHORIZED: 401,
  CONFLICT: 409,
  VERSION_CONFLICT: 409,
  RATE_LIMITED: 429,
  GONE: 410,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
};

export class DomainError extends Error {
  readonly code: DomainErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: DomainErrorCode, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "DomainError";
    this.code = code;
    this.status = HTTP_STATUS[code];
    this.details = details;
  }

  toJSON(): {
    code: DomainErrorCode;
    message: string;
    details?: Record<string, unknown>;
  } {
    return this.details === undefined
      ? { code: this.code, message: this.message }
      : { code: this.code, message: this.message, details: this.details };
  }
}

export const validationError = (message: string, details?: Record<string, unknown>) =>
  new DomainError("VALIDATION", message, details);

/**
 * Recurso ausente OU fora do escopo do solicitante.
 *
 * Deliberadamente indistinguível de "existe mas você não pode ver": responder
 * 403 para um recurso de outra organização vazaria sua existência. Esta é a
 * defesa contra IDOR por enumeração.
 */
export const notFound = (resource: string) =>
  new DomainError("NOT_FOUND", `${resource} não encontrado`);

export const forbidden = (message = "Ação não permitida para este acesso") =>
  new DomainError("FORBIDDEN", message);

export const unauthorized = (message = "Autenticação necessária") =>
  new DomainError("UNAUTHORIZED", message);

export const versionConflict = (details: Record<string, unknown>) =>
  new DomainError(
    "VERSION_CONFLICT",
    "A entrega foi alterada por outra pessoa. Recarregue para ver a versão atual.",
    details,
  );

export const conflict = (message: string) => new DomainError("CONFLICT", message);

export const gone = (message: string) => new DomainError("GONE", message);

export const rateLimited = (retryAfterSeconds: number) =>
  new DomainError("RATE_LIMITED", "Muitas tentativas. Tente novamente em instantes.", {
    retryAfterSeconds,
  });

export const payloadTooLarge = (message: string) => new DomainError("PAYLOAD_TOO_LARGE", message);

export const unsupportedMedia = (message: string) =>
  new DomainError("UNSUPPORTED_MEDIA_TYPE", message);

export function isDomainError(error: unknown): error is DomainError {
  return error instanceof DomainError;
}
